/**
 * The one manual-sync path for the Bexio surfaces.
 *
 * Two pages offer the action — the overview and the legacy page that still hosts
 * the mapping modals — and they must not grow two copies of it. There is exactly
 * one endpoint behind this (`POST /api/admin/bexio/sync`, admin JWT, never the
 * cron secret route) and exactly one set of German toast messages.
 *
 * `onFinished` is how a page refreshes itself afterwards; the hook itself knows
 * nothing about what either page displays.
 */
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { triggerBexioSync } from "../../../api/adminData/bexio";

export function useBexioSync(onFinished) {
  const [syncing, setSyncing] = useState(false);

  const sync = useCallback(async () => {
    // Guard against a double click starting a second run.
    if (syncing) return;
    setSyncing(true);
    const toastId = toast.loading("Bexio-Synchronisation läuft …");
    try {
      const res = await triggerBexioSync();
      if (res?.status === "success") {
        toast.success("Synchronisation erfolgreich.", { id: toastId });
      } else if (res?.status === "skipped") {
        toast.message(res?.detail || "Synchronisation übersprungen.", { id: toastId });
      } else {
        toast.error(res?.detail || "Synchronisation fehlgeschlagen.", { id: toastId });
      }
      // Refresh even on a reported failure: a partial run still changed data.
      if (onFinished) await onFinished();
    } catch (e) {
      toast.error(e?.message || "Synchronisation fehlgeschlagen.", { id: toastId });
    } finally {
      setSyncing(false);
    }
  }, [syncing, onFinished]);

  return { syncing, sync };
}

export default useBexioSync;
